import React, { useState, useRef } from 'react';
import { Eye, Download, Printer, ExternalLink, FileText, Image, File, X } from 'lucide-react';
import { toast } from 'sonner';

const DocumentPreview = ({ document, onDownload, onPrint, dropboxUrl }) => {
  const [showPreview, setShowPreview] = useState(false);
  const [previewPosition, setPreviewPosition] = useState({ x: 0, y: 0 });
  const [isHovering, setIsHovering] = useState(false);
  const hoverTimeoutRef = useRef(null);
  const containerRef = useRef(null);

  const getFileIcon = (filename) => {
    const ext = filename?.split('.').pop()?.toLowerCase();
    if (['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp'].includes(ext)) {
      return <Image className="text-green-600" size={20} />;
    } else if (['pdf'].includes(ext)) {
      return <FileText className="text-red-600" size={20} />;
    }
    return <File className="text-blue-600" size={20} />;
  };

  const getFileType = (filename) => {
    const ext = filename?.split('.').pop()?.toLowerCase();
    if (['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp'].includes(ext)) {
      return 'image';
    } else if (['pdf'].includes(ext)) {
      return 'pdf';
    }
    return 'other';
  };

  const handleMouseEnter = (e) => {
    const rect = containerRef.current?.getBoundingClientRect();
    if (rect) {
      // Position preview to the right of the document card
      setPreviewPosition({
        x: rect.right + 10,
        y: Math.min(rect.top, window.innerHeight - 450)
      });
    }
    
    hoverTimeoutRef.current = setTimeout(() => {
      setIsHovering(true);
      setShowPreview(true);
    }, 300);
  };

  const handleMouseLeave = () => {
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
    }
    setIsHovering(false);
    setShowPreview(false);
  };

  const handleDownload = () => {
    if (onDownload) {
      onDownload(document);
    } else if (document.url) {
      window.open(document.url, '_blank');
    }
  };

  const handlePrint = () => {
    if (onPrint) {
      onPrint(document);
    } else if (document.url) {
      const printWindow = window.open(document.url, '_blank');
      printWindow.onload = () => {
        printWindow.print();
      };
    }
    toast.success('Opening print dialog...');
  };

  const fileType = getFileType(document.filename || document.name);
  const previewUrl = document.url || document.preview_url;

  return (
    <div 
      ref={containerRef}
      className="relative"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {/* Document Card */}
      <div className="border border-slate-200 rounded-lg p-4 hover:border-purple-300 hover:shadow-md transition-all cursor-pointer bg-white">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-lg bg-slate-100 flex items-center justify-center flex-shrink-0">
            {getFileIcon(document.filename || document.name)}
          </div>
          <div className="flex-1 min-w-0">
            <h4 className="font-medium text-slate-900 text-sm truncate">
              {document.filename || document.name || 'Document'}
            </h4>
            {document.uploaded_at && (
              <p className="text-xs text-slate-500 mt-1">
                Uploaded {new Date(document.uploaded_at).toLocaleDateString()}
              </p>
            )}
            {document.size && (
              <p className="text-xs text-slate-400">
                {(document.size / 1024).toFixed(1)} KB
              </p>
            )}
          </div>
          
          {/* Quick Actions */}
          <div className="flex gap-1">
            <button
              onClick={(e) => { e.stopPropagation(); setShowPreview(true); }}
              className="p-2 text-slate-400 hover:text-purple-600 hover:bg-purple-50 rounded-lg transition-colors"
              title="Preview"
            >
              <Eye size={16} />
            </button>
            <button
              onClick={(e) => { e.stopPropagation(); handleDownload(); }}
              className="p-2 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
              title="Download"
            >
              <Download size={16} />
            </button>
            <button
              onClick={(e) => { e.stopPropagation(); handlePrint(); }}
              className="p-2 text-slate-400 hover:text-green-600 hover:bg-green-50 rounded-lg transition-colors"
              title="Print"
            >
              <Printer size={16} />
            </button>
            {dropboxUrl && (
              <a
                href={dropboxUrl}
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => e.stopPropagation()}
                className="p-2 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                title="View in Dropbox"
              >
                <ExternalLink size={16} />
              </a>
            )}
          </div>
        </div>
      </div>

      {/* Hover Preview Popup */}
      {showPreview && previewUrl && (
        <div 
          className="fixed z-50 bg-white rounded-lg shadow-2xl border border-slate-200 overflow-hidden"
          style={{
            left: Math.min(previewPosition.x, window.innerWidth - 420),
            top: previewPosition.y,
            width: '400px',
            maxHeight: '450px'
          }}
          onMouseEnter={() => setShowPreview(true)}
          onMouseLeave={handleMouseLeave}
        >
          {/* Preview Header */}
          <div className="bg-slate-100 px-4 py-3 flex justify-between items-center border-b border-slate-200">
            <div className="flex items-center gap-2">
              {getFileIcon(document.filename || document.name)}
              <span className="font-medium text-sm text-slate-900 truncate max-w-[280px]">
                {document.filename || document.name}
              </span>
            </div>
            <button 
              onClick={() => setShowPreview(false)}
              className="text-slate-400 hover:text-slate-600"
            >
              <X size={18} />
            </button>
          </div>

          {/* Preview Content */}
          <div className="p-4 bg-slate-50 h-[320px] flex items-center justify-center">
            {fileType === 'image' ? (
              <img 
                src={previewUrl} 
                alt={document.filename || 'Preview'} 
                className="max-w-full max-h-full object-contain rounded"
              />
            ) : fileType === 'pdf' ? (
              <iframe
                src={`${previewUrl}#toolbar=0&navpanes=0`}
                title="Document Preview"
                className="w-full h-full border-0 rounded"
              />
            ) : (
              <div className="text-center text-slate-500">
                <FileText size={48} className="mx-auto mb-2 text-slate-400" />
                <p className="text-sm">Preview not available</p>
                <p className="text-xs text-slate-400 mt-1">Click download to view</p>
              </div>
            )}
          </div>

          {/* Preview Footer Actions */}
          <div className="bg-white px-4 py-3 border-t border-slate-200 flex justify-end gap-2">
            <button
              onClick={handleDownload}
              className="flex items-center gap-1 px-3 py-1.5 bg-blue-50 text-blue-700 rounded-lg text-sm hover:bg-blue-100 transition-colors"
            >
              <Download size={14} />
              Download
            </button>
            <button
              onClick={handlePrint}
              className="flex items-center gap-1 px-3 py-1.5 bg-green-50 text-green-700 rounded-lg text-sm hover:bg-green-100 transition-colors"
            >
              <Printer size={14} />
              Print
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default DocumentPreview;
